import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Checkbox,
  DatePicker,
  FormControlLabel,
  InputLabel,
  Link,
  SvgAction,
} from '@hopskipdrive/react-components';
import { Tooltip } from '@mui/material';
import { SearchNavBar } from '@rideiq/shared/components';
import { Button } from '@rideiq/shared/components/Button';
import { colorPalette } from '@rideiq/shared/components/variables';
import { isValid } from 'date-fns';
import includes from 'lodash/includes';

import { analyticsPage, analyticsViewAllCanceledChecked } from '../../../api/services/Analytics';
import { RIDE_STATES_QUERY } from '../../../constants';
import { useQuery } from '../../../hooks/useQuery';
import { useTimezone } from '../../../hooks/useTimezone';
import type { useRidesPaginatedQuery } from '../../hooks/useRidesPaginatedQuery';
import type { useRidesQuery } from '../../hooks/useRidesQuery';
import { RefreshDisplay } from '../RideSync';

import { DateFilterContainer, DateSearchContainer, FilterContainer, FilterPageFilters, TooltipBubble } from './styles';

interface FilterSectionProps {
  ridesSource: ReturnType<typeof useRidesQuery> | ReturnType<typeof useRidesPaginatedQuery>;
  handleOnFiltered: () => void;
  activeFilterCount?: number;
  hasActiveFilters?: boolean;
  onOpenFilters?: () => void;
  onClearAllFilters?: () => void;
}

const UPCOMING_STATE = RIDE_STATES_QUERY.upcoming;
const REQUESTED_STATE = RIDE_STATES_QUERY.requested;
const CANCELED_STATES = [RIDE_STATES_QUERY.allCanceled, RIDE_STATES_QUERY.billableCanceled];

export const FilterSection: React.FC<FilterSectionProps> = ({
  ridesSource,
  handleOnFiltered,
  activeFilterCount = 0,
  hasActiveFilters = false,
  onOpenFilters,
  onClearAllFilters,
}) => {
  const { queryParams, debounceHandleQuery, lastFetch, isFetching } = ridesSource;
  const [, setSearchParams] = useSearchParams({ state: 'all' });

  const { getStartOfDate, getEndOfDate, getDateInTimezone } = useTimezone();
  const isCurrentStateCancel = includes(CANCELED_STATES, queryParams.state);
  const [fromDate, setFromDate] = useState<Date | null>(
    isCurrentStateCancel || queryParams.state === REQUESTED_STATE ? new Date() : null
  );
  const [hasDateError, setHasDateError] = useState<boolean>(false);
  const dateLabel = isCurrentStateCancel ? 'Date' : 'Jump To';
  const searchPlaceholder = 'Search Riders, tags, or locations';
  const canClearDate = !isCurrentStateCancel;
  const currentTab =
    queryParams.state === RIDE_STATES_QUERY.billableCanceled
      ? RIDE_STATES_QUERY.allCanceled
      : queryParams.state || UPCOMING_STATE;

  const getDateForState = (date, state) => {
    return date
      ? ([UPCOMING_STATE, REQUESTED_STATE].indexOf(state) !== -1
          ? getStartOfDate(date)
          : getEndOfDate(date)
        ).toISOString()
      : date;
  };

  const handleOnlyBilledCancelCheckbox = (e) => {
    if (e.target.checked) {
      debounceHandleQuery(queryParams.query || '', RIDE_STATES_QUERY.billableCanceled, queryParams.fromDate);
    } else {
      debounceHandleQuery(queryParams.query || '', RIDE_STATES_QUERY.allCanceled, queryParams.fromDate);
    }
    analyticsViewAllCanceledChecked(e.target.checked);
    handleOnFiltered();
  };

  const handleDateFilter = async (date?: Date | string | null) => {
    if (date || (queryParams.fromDate && !date)) {
      !date && setFromDate(null);
      debounceHandleQuery(queryParams.query || '', queryParams.state, getDateForState(date, queryParams.state));
      handleOnFiltered();
    }
  };

  const handleKeyboardDateFilter = async (e) => {
    if (e.key === 'Enter') {
      const inputValue = e.target.value.replace(/[^0-9\/]/g, '');

      if (isCurrentStateCancel && !inputValue) {
        // Do not save the date from the canceled state date input when moving to a new tab
        setFromDate(queryParams.fromDate ? getDateInTimezone(queryParams.fromDate) : new Date());

        return;
      }
      let autoFilledDate;

      // handle partial dates with at least month and day
      if (inputValue.length === 6) {
        const newDate = new Date(`${inputValue?.slice(0, 6)}${new Date().getFullYear()}`);

        // only use new date if it is a valid
        if (newDate.getTime()) {
          autoFilledDate = newDate;
          setFromDate(newDate);
        }
      }

      const inputDate = autoFilledDate || (inputValue && new Date(inputValue)) || fromDate;

      if ((inputDate && isValid(new Date(inputDate))) || !inputDate) {
        handleDateFilter(inputDate);
      } else {
        setHasDateError(true);
      }
    }
  };

  const handleQuerySearch = (query) => {
    debounceHandleQuery(query || '', queryParams.state, queryParams.fromDate);
    handleOnFiltered();
  };

  const refreshTable = () => {
    debounceHandleQuery(queryParams.query || '', 'all', queryParams.fromDate);
  };

  useQuery(onOpenFilters ? {} : { fromDate: queryParams.fromDate || '', query: queryParams.query }, (init) => {
    debounceHandleQuery(
      init.get('query') || '',
      queryParams.state,
      init.get('fromDate') || fromDate?.toISOString() || queryParams.fromDate
    );

    if (init.get('fromDate')) {
      setFromDate(getDateInTimezone(init.get('fromDate')));
    }
    handleOnFiltered();
  });

  useEffect(() => {
    analyticsPage('Rides', { tab: 'In Progress & Upcoming' });
  }, []);

  useEffect(() => {
    if (onOpenFilters) {
      return;
    }
    setSearchParams((prevParams) => {
      return new URLSearchParams({
        ...Object.fromEntries(prevParams.entries()),
        ...{ state: queryParams.state },
      });
    });
  }, [queryParams.state, onOpenFilters]);

  useEffect(() => {
    if (isCurrentStateCancel || queryParams.state === REQUESTED_STATE) {
      setFromDate(queryParams.fromDate ? getDateInTimezone(queryParams.fromDate) : new Date());
    } else {
      setFromDate(queryParams.fromDate ? getDateInTimezone(queryParams.fromDate) : null);
    }
  }, [getDateInTimezone, isCurrentStateCancel, queryParams.fromDate, queryParams.state]);

  return (
    <FilterContainer>
      <FilterPageFilters>
        <DateSearchContainer>
          <DateFilterContainer>
            <InputLabel htmlFor="date-picker" style={{ minWidth: dateLabel === 'Jump To' ? '52px' : 0 }}>
              {dateLabel}
            </InputLabel>
            <DatePicker
              onChange={(date) => {
                if (!date || (date instanceof Date && date.getTime())) {
                  setFromDate(date);
                  setHasDateError(false);
                }
              }}
              value={fromDate}
              onAccept={(date) => handleDateFilter(date)}
              slots={{
                clearButton: () =>
                  canClearDate ? (
                    <Tooltip title="Delete date">
                      <TooltipBubble>
                        <Link
                          tabIndex={0}
                          onClick={() => handleDateFilter()}
                          onKeyDown={(e) => {
                            e.preventDefault();
                            (e.key === 'Enter' || e.key === ' ') && handleDateFilter();
                          }}
                        >
                          <SvgAction icon="close" display={queryParams.fromDate ? '' : 'none'} />
                        </Link>
                      </TooltipBubble>
                    </Tooltip>
                  ) : null,
              }}
              slotProps={{
                field: {
                  clearable: canClearDate,
                },
                textField: {
                  id: 'date-picker',
                  error: hasDateError,
                  helperText: hasDateError ? 'Please enter a valid date' : '',
                  onKeyDown: handleKeyboardDateFilter,
                  inputProps: {
                    'data-testid': 'rides-date-picker',
                  },
                },
              }}
            />
          </DateFilterContainer>
          <SearchNavBar
            id="search-field"
            debounceHandleQuery={handleQuerySearch}
            placeholder={searchPlaceholder}
            query={queryParams.query}
          />
          {onOpenFilters && (
            <Box position="relative" display="inline-flex" alignItems="center" flexShrink={0}>
              <Button color="secondary" onClick={onOpenFilters} size="medium">
                Filters
              </Button>
              {activeFilterCount > 0 && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-8px',
                    right: '-10px',
                    bgcolor: colorPalette.brandNavy,
                    color: colorPalette.neutral100,
                    borderRadius: '10px',
                    minWidth: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    px: '4px',
                  }}
                >
                  {activeFilterCount}
                </Box>
              )}
            </Box>
          )}
          {onClearAllFilters && hasActiveFilters && (
            <Box
              display="flex"
              alignItems="center"
              flexShrink={0}
              sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              onClick={onClearAllFilters}
            >
              <Box component="span" sx={{ color: colorPalette.neutral10, fontSize: 13 }}>
                Clear all
              </Box>
            </Box>
          )}
        </DateSearchContainer>
        {isCurrentStateCancel && (
          <FormControlLabel
            value={RIDE_STATES_QUERY.billableCanceled}
            control={
              <Checkbox
                color="primary"
                onClick={handleOnlyBilledCancelCheckbox}
                checked={queryParams.state === RIDE_STATES_QUERY.billableCanceled}
              />
            }
            label="Show only billed cancels"
            labelPlacement="end"
          />
        )}
        {currentTab === RIDE_STATES_QUERY.upcoming && (
          <RefreshDisplay lastFetch={lastFetch} isFetching={isFetching} refresh={refreshTable} />
        )}
      </FilterPageFilters>
    </FilterContainer>
  );
};
